import React from 'react';
import { X, CheckCircle, Loader2 } from 'lucide-react';

interface ConfirmReactivateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void>;
    isReactivating: boolean;
    user: {
        firstName: string;
        lastName: string;
        email: string;
        role: string;
    } | null;
}

const ConfirmReactivateModal: React.FC<ConfirmReactivateModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    isReactivating,
    user
}) => {
    const handleConfirm = async () => {
        await onConfirm();
    };

    const formatRoleName = (role: string) => {
        const roleNames: { [key: string]: string } = {
            'super_admin': 'Super Admin',
            'admin': 'Admin',
            'manager': 'Manager',
            'compliance': 'Compliance',
            'user': 'User'
        };
        return roleNames[role] || role;
    };

    if (!isOpen || !user) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Reactivate User</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={isReactivating}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        Are you sure you want to reactivate <strong>{user.firstName} {user.lastName}</strong>?
                    </p>

                    <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                        <p className="text-sm text-green-800">
                            <strong>What happens when you reactivate a user:</strong>
                        </p>
                        <ul className="mt-2 text-sm text-green-800 list-disc list-inside space-y-1">
                            <li>The user will be able to log in again</li>
                            <li>They will retain their previous role: <strong>{formatRoleName(user.role)}</strong></li>
                            <li>All their historical data will be accessible</li>
                            <li>Email: <span className="font-mono">{user.email}</span></li>
                        </ul>
                    </div>

                    <p className="text-sm text-gray-600">
                        The user will regain access to the system immediately after reactivation.
                    </p>
                </div>

                {/* Footer */}
                <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isReactivating}
                        className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isReactivating}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex items-center"
                    >
                        {isReactivating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {isReactivating ? 'Reactivating...' : 'Reactivate User'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmReactivateModal;